import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import Paywall from '../components/Paywall';
import { 
    Users, UserPlus, Check, X, Eye, Loader2, MessageCircle, Send, 
    Mail, Crown, Lock, AlertCircle, Heart
} from 'lucide-react';

const PartnersPage = () => {
    const { user, api } = useAuth();
    const [partners, setPartners] = useState([]);
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteMessage, setInviteMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [sendingReport, setSendingReport] = useState(false);
    const [viewingPartner, setViewingPartner] = useState(null);
    const [partnerMoods, setPartnerMoods] = useState(null);
    const [chatOpen, setChatOpen] = useState(null);
    const [chatMessages, setChatMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [sendingMessage, setSendingMessage] = useState(false);
    const [showPaywall, setShowPaywall] = useState({ show: false, type: '', feature: '' });
    const chatEndRef = useRef(null);
    const wsRef = useRef(null);

    const subscription = user?.subscription || { tier: 'basic', addons: [] };
    const tier = subscription.tier || 'basic';
    const hasChat = tier !== 'basic' || subscription.addons?.includes('partners_pro');
    
    // Partner limits based on tier
    const partnerLimits = {
        basic: 1,
        plus: 3,
        complete: -1 // unlimited
    };
    const maxPartners = partnerLimits[tier] || 1;
    const acceptedPartners = partners.filter(p => p.status === 'accepted' || p.status === 'pending').length;
    const canAddMore = maxPartners === -1 || acceptedPartners < maxPartners;

    useEffect(() => {
        fetchData();
        setupWebSocket();
        return () => {
            if (wsRef.current) {
                wsRef.current.close();
            }
        };
    }, []);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [chatMessages]);

    const setupWebSocket = () => {
        const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
        if (!wsUrl || !user?.id) return;

        try {
            wsRef.current = new WebSocket(`${wsUrl}/ws/chat/${user.id}`);
            
            wsRef.current.onmessage = (event) => {
                const data = JSON.parse(event.data);
                if (data.type === 'new_message') {
                    setChatMessages(prev => [...prev, data.data]);
                    toast.info(`New message from ${data.data.sender_name}`);
                }
            };

            wsRef.current.onclose = () => {
                setTimeout(setupWebSocket, 3000);
            };
        } catch (error) {
            console.error('WebSocket error:', error);
        }
    };

    const fetchData = async () => {
        try {
            const [partnersRes, requestsRes] = await Promise.all([
                api.get('/partners/'),
                api.get('/partners/requests')
            ]);
            setPartners(partnersRes.data);
            setRequests(requestsRes.data);
        } catch (error) {
            console.error('Failed to fetch partners:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        if (!inviteEmail.trim()) {
            toast.error('Please enter an email address');
            return;
        }

        if (!canAddMore) {
            if (tier === 'basic') {
                setShowPaywall({ show: true, type: 'partners_pro', feature: 'More Partners' });
            } else if (tier === 'plus') {
                setShowPaywall({ show: true, type: 'complete', feature: 'Unlimited Partners' });
            }
            return;
        }

        setSending(true);
        try {
            const response = await api.post('/partners/invite', {
                partner_email: inviteEmail,
                message: inviteMessage || null
            });
            setPartners([...partners, response.data]);
            setInviteEmail('');
            setInviteMessage('');
            toast.success('Invitation sent! Your partner will receive an email with your mood report.');
        } catch (error) {
            if (error.response?.status === 403) {
                setShowPaywall({ show: true, type: 'partners_pro', feature: 'More Partners' });
            } else {
                toast.error(error.response?.data?.detail || 'Failed to send invitation');
            }
        } finally {
            setSending(false);
        }
    };

    const handleSendMoodReport = async () => {
        const acceptedCount = partners.filter(p => p.status === 'accepted').length;
        if (acceptedCount === 0) {
            toast.error('No accepted partners to send report to');
            return;
        }

        setSendingReport(true);
        try {
            const response = await api.post('/partners/send-mood-report');
            toast.success(response.data.message);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to send mood report');
        } finally {
            setSendingReport(false);
        }
    };

    const handleRespond = async (partnerId, accept) => {
        try {
            await api.put(`/partners/${partnerId}/respond?accept=${accept}`);
            setRequests(requests.filter(r => r.id !== partnerId));
            if (accept) {
                fetchData();
            }
            toast.success(accept ? 'Partner request accepted!' : 'Partner request declined');
        } catch (error) {
            toast.error('Failed to respond to request');
        }
    };

    const viewPartnerMoods = async (partnerId) => {
        setViewingPartner(partnerId);
        try {
            const response = await api.get(`/partners/${partnerId}/mood-summary`);
            setPartnerMoods(response.data);
        } catch (error) {
            toast.error('Unable to view partner moods');
            setViewingPartner(null);
        }
    };

    const openChat = async (partnerId) => {
        if (!hasChat) {
            setShowPaywall({ show: true, type: 'partners_pro', feature: 'Partners Chat' });
            return;
        }

        setChatOpen(partnerId);
        try {
            const response = await api.get(`/chat/messages/${partnerId}`);
            setChatMessages(response.data.messages || []);
        } catch (error) {
            if (error.response?.status === 403) {
                setShowPaywall({ show: true, type: 'partners_pro', feature: 'Partners Chat' });
                setChatOpen(null);
            } else {
                toast.error('Failed to load messages');
            }
        }
    };

    const sendChatMessage = async () => {
        if (!newMessage.trim() || !chatOpen) return;

        setSendingMessage(true);
        try {
            await api.post('/chat/send', {
                partner_id: chatOpen,
                message: newMessage
            });
            setChatMessages(prev => [...prev, {
                id: Date.now().toString(),
                sender_id: user.id,
                sender_name: user.name,
                message: newMessage,
                created_at: new Date().toISOString()
            }]);
            setNewMessage('');
        } catch (error) {
            toast.error('Failed to send message');
        } finally {
            setSendingMessage(false);
        }
    };

    const handleUpgrade = () => {
        setShowPaywall({ show: false, type: '', feature: '' });
        window.location.href = '/pricing';
    };

    return (
        <DashboardLayout>
            <div data-testid="partners-page" className="max-w-3xl mx-auto space-y-6">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="font-heading text-3xl font-semibold text-foreground">
                            Accountability Partners
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Share your journey with trusted friends or family
                        </p>
                    </div>
                    
                    {/* Tier Badge */}
                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10">
                        <Crown className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium capitalize">{tier}</span>
                        <span className="text-xs text-muted-foreground">
                            ({maxPartners === -1 ? '∞' : `${acceptedPartners}/${maxPartners}`} partners)
                        </span>
                    </div>
                </div>

                {/* Subscription Tier Info */}
                {tier === 'basic' && (
                    <Card className="glass-card border-amber-500/30 bg-amber-500/5">
                        <CardContent className="p-4 flex items-center gap-4">
                            <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                            <div className="flex-1">
                                <p className="text-sm font-medium">Basic Plan Limitations</p>
                                <p className="text-xs text-muted-foreground">
                                    You can have 1 partner and view their moods. Upgrade to Plus for chat and up to 3 partners.
                                </p>
                            </div>
                            <Button 
                                size="sm" 
                                onClick={() => window.location.href = '/pricing'}
                                className="rounded-full"
                            >
                                Upgrade
                            </Button>
                        </CardContent>
                    </Card>
                )}

                {/* Send Mood Report Button */}
                {partners.some(p => p.status === 'accepted') && (
                    <Card className="glass-card bg-gradient-to-r from-primary/5 to-accent/5">
                        <CardContent className="p-4 flex items-center justify-between">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                                    <Heart className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="font-medium">Share Your Mood Report</p>
                                    <p className="text-sm text-muted-foreground">
                                        Send a summary of how you're doing to your partners
                                    </p>
                                </div>
                            </div>
                            <Button
                                onClick={handleSendMoodReport}
                                disabled={sendingReport}
                                className="rounded-full"
                                data-testid="send-mood-report-btn"
                            >
                                {sendingReport ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <Mail className="w-4 h-4 mr-2" />
                                )}
                                Send Report
                            </Button>
                        </CardContent>
                    </Card>
                )}

                {/* Invite Form */}
                <Card className="glass-card">
                    <CardHeader>
                        <CardTitle className="font-heading text-xl flex items-center gap-2">
                            <UserPlus className="w-5 h-5" />
                            Invite an Accountability Partner
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleInvite} className="space-y-4">
                            <div className="space-y-2">
                                <Label>Partner's Email</Label>
                                <Input
                                    type="email"
                                    value={inviteEmail}
                                    onChange={(e) => setInviteEmail(e.target.value)}
                                    placeholder="friend@example.com"
                                    className="h-12 rounded-xl"
                                    data-testid="partner-email-input"
                                    disabled={!canAddMore}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Personal Message (optional)</Label>
                                <Input
                                    value={inviteMessage}
                                    onChange={(e) => setInviteMessage(e.target.value)}
                                    placeholder="I'd like you to be my accountability partner..."
                                    className="h-12 rounded-xl"
                                    data-testid="partner-message-input"
                                    disabled={!canAddMore}
                                />
                            </div>
                            
                            {!canAddMore && (
                                <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-500/10 text-amber-600">
                                    <Lock className="w-4 h-4" />
                                    <span className="text-sm">
                                        {tier === 'basic' 
                                            ? 'Upgrade to Plus for up to 3 partners' 
                                            : 'Upgrade to Complete for unlimited partners'}
                                    </span>
                                </div>
                            )}
                            
                            <Button 
                                type="submit" 
                                disabled={sending || !canAddMore}
                                className="rounded-full"
                                data-testid="send-invite-btn"
                            >
                                {sending ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                ) : (
                                    <UserPlus className="w-4 h-4 mr-2" />
                                )}
                                Send Invitation
                            </Button>
                        </form>
                    </CardContent>
                </Card>

                {/* Pending Requests */}
                {requests.length > 0 && (
                    <Card className="glass-card border-chart-2/30">
                        <CardHeader>
                            <CardTitle className="font-heading text-xl">Partner Requests</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {requests.map((request) => (
                                <div 
                                    key={request.id}
                                    className="flex items-center justify-between p-4 rounded-xl bg-muted/30"
                                >
                                    <div>
                                        <p className="font-medium">{request.requester?.name}</p>
                                        <p className="text-sm text-muted-foreground">
                                            {request.requester?.email}
                                        </p>
                                        {request.message && (
                                            <p className="text-sm text-muted-foreground mt-1 italic">
                                                "{request.message}"
                                            </p>
                                        )}
                                    </div>
                                    <div className="flex gap-2">
                                        <Button
                                            size="sm"
                                            onClick={() => handleRespond(request.id, true)}
                                            className="rounded-full"
                                            data-testid={`accept-request-${request.id}`}
                                        >
                                            <Check className="w-4 h-4" />
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => handleRespond(request.id, false)}
                                            className="rounded-full"
                                            data-testid={`decline-request-${request.id}`}
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </CardContent>
                    </Card>
                )}

                {/* My Partners */}
                <Card className="glass-card">
                    <CardHeader>
                        <CardTitle className="font-heading text-xl flex items-center gap-2">
                            <Users className="w-5 h-5" />
                            My Partners
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="text-center py-8">
                                <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                            </div>
                        ) : partners.length === 0 ? (
                            <div className="text-center py-8">
                                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                                <p className="text-muted-foreground">
                                    No accountability partners yet. Invite someone you trust!
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {partners.map((partner) => (
                                    <div 
                                        key={partner.id}
                                        className="flex items-center justify-between p-4 rounded-xl bg-muted/30"
                                    >
                                        <div>
                                            <p className="font-medium">{partner.partner_email}</p>
                                            <p className={`text-sm ${
                                                partner.status === 'accepted' 
                                                    ? 'text-chart-1' 
                                                    : partner.status === 'pending'
                                                        ? 'text-chart-4'
                                                        : 'text-destructive'
                                            }`}>
                                                {partner.status === 'accepted' && '✓ Connected'}
                                                {partner.status === 'pending' && '⏳ Pending'}
                                                {partner.status === 'declined' && '✗ Declined'}
                                            </p>
                                        </div>
                                        {partner.status === 'accepted' && partner.partner_id && (
                                            <div className="flex gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => viewPartnerMoods(partner.partner_id)}
                                                    className="rounded-full"
                                                    data-testid={`view-partner-${partner.id}`}
                                                >
                                                    <Eye className="w-4 h-4 mr-1" />
                                                    Moods
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    variant={hasChat ? "outline" : "ghost"}
                                                    onClick={() => openChat(partner.partner_id)}
                                                    className="rounded-full"
                                                    data-testid={`chat-partner-${partner.id}`}
                                                >
                                                    {!hasChat && <Lock className="w-3 h-3 mr-1" />}
                                                    <MessageCircle className="w-4 h-4 mr-1" />
                                                    Chat
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </CardContent>
                </Card>

                {/* Partner Mood View Modal */}
                {partnerMoods && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
                        onClick={() => { setPartnerMoods(null); setViewingPartner(null); }}
                    >
                        <motion.div
                            initial={{ scale: 0.95 }}
                            animate={{ scale: 1 }}
                            className="bg-card rounded-2xl p-6 max-w-md w-full"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="font-heading text-xl mb-4">
                                {partnerMoods.user?.name}'s Recent Moods
                            </h3>
                            <div className="flex items-center gap-4 mb-4">
                                <p className="text-muted-foreground">
                                    Average mood: <span className="font-semibold text-foreground">{partnerMoods.average_mood.toFixed(1)}/10</span>
                                </p>
                                {partnerMoods.mood_streak > 0 && (
                                    <span className="px-2 py-1 rounded-full bg-chart-4/20 text-chart-4 text-sm">
                                        🔥 {partnerMoods.mood_streak} day streak
                                    </span>
                                )}
                            </div>
                            <div className="space-y-3 max-h-64 overflow-y-auto">
                                {partnerMoods.recent_moods.map((mood, index) => (
                                    <div key={index} className="flex items-center gap-3 p-3 rounded-xl bg-muted/30">
                                        <span className="text-2xl">{mood.emoji}</span>
                                        <div className="flex-1">
                                            <p className="font-medium">{mood.mood_rating}/10</p>
                                            <p className="text-sm text-muted-foreground">
                                                {new Date(mood.created_at).toLocaleDateString()}
                                            </p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <Button
                                onClick={() => { setPartnerMoods(null); setViewingPartner(null); }}
                                className="w-full mt-4 rounded-full"
                            >
                                Close
                            </Button>
                        </motion.div>
                    </motion.div>
                )}

                {/* Chat Modal */}
                {chatOpen && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
                        onClick={() => setChatOpen(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.95 }}
                            animate={{ scale: 1 }}
                            className="bg-card rounded-2xl p-6 max-w-lg w-full h-[500px] flex flex-col"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="font-heading text-xl flex items-center gap-2">
                                    <MessageCircle className="w-5 h-5" />
                                    Partner Chat
                                </h3>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setChatOpen(null)}
                                >
                                    <X className="w-5 h-5" />
                                </Button>
                            </div>

                            {/* Messages */}
                            <div className="flex-1 overflow-y-auto space-y-3 mb-4 p-2">
                                {chatMessages.length === 0 ? (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                        <p>No messages yet. Start the conversation!</p>
                                    </div>
                                ) : (
                                    chatMessages.map((msg, index) => (
                                        <div
                                            key={msg.id || index}
                                            className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                                        >
                                            <div
                                                className={`max-w-[80%] p-3 rounded-2xl ${
                                                    msg.sender_id === user.id
                                                        ? 'bg-primary text-primary-foreground'
                                                        : 'bg-muted'
                                                }`}
                                            >
                                                <p className="text-sm">{msg.message}</p>
                                                <p className={`text-xs mt-1 ${
                                                    msg.sender_id === user.id ? 'text-primary-foreground/70' : 'text-muted-foreground'
                                                }`}>
                                                    {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </p>
                                            </div>
                                        </div>
                                    ))
                                )}
                                <div ref={chatEndRef} />
                            </div>

                            {/* Input */}
                            <div className="flex gap-2">
                                <Input
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    placeholder="Type a message..."
                                    className="rounded-full"
                                    onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                                    data-testid="chat-input"
                                />
                                <Button
                                    onClick={sendChatMessage}
                                    disabled={sendingMessage || !newMessage.trim()}
                                    className="rounded-full"
                                    size="icon"
                                    data-testid="send-message-btn"
                                >
                                    {sendingMessage ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Send className="w-4 h-4" />
                                    )}
                                </Button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}

                {/* Paywall */}
                <Paywall
                    type={showPaywall.type}
                    isOpen={showPaywall.show}
                    onClose={() => setShowPaywall({ show: false, type: '', feature: '' })}
                    onUpgrade={handleUpgrade}
                    feature={showPaywall.feature}
                />
            </div>
        </DashboardLayout>
    );
};

export default PartnersPage;
